// Sugalarm — cycle 468: the subscription lock on the client, as PURE rules.
//
// The server is the only authority: `GET /api/me/subscription` says whether
// THIS account is locked (`service_state`, the stamp — never the clock), and
// the WS `service_state` frame says whether a MEMBER this device can see is
// locked (its owner's lock). Nothing here decides entitlement; these helpers
// only turn the server's two words into what each surface shows.
//
// The copy is the operator's (2026-10-09 / 2026-10-27), verbatim. New copy
// uses "Sugar Alarm"; no existing string changes.

import type { Subscription } from "../api/subscription";
import { shortDate, type BillingPlatform } from "./billing";

/** The lockout page's sentence, on every surface (operator, verbatim). */
export const LAPSED_SENTENCE =
  "The 30 day grace period has lapsed. Please reactivate the subscription in the app.";

/** A hero following a locked member sees this in place of readings. */
export function followerStoppedLine(memberName: string): string {
  const name = memberName.trim() || "this member";
  return (
    `Sugar Alarm monitoring for ${name} has stopped. The 30 day grace period has lapsed. ` +
    "The account owner can reactivate the subscription in the app."
  );
}

/** The owner's grace banner line; null without a date to name. */
export function graceBannerLine(graceEndsAt: string | null | undefined): string | null {
  const d = shortDate(graceEndsAt);
  if (!d) return null;
  return `Your Sugar Alarm grace period ends ${d}. Reactivate in the app to keep alarms running.`;
}

/** THIS account is locked: the server's stamp, never a derived state. */
export function ownerLocked(sub: Pick<Subscription, "service_state" | "locked_at"> | null | undefined): boolean {
  if (!sub) return false;
  return sub.service_state === "locked" || (sub.service_state == null && sub.locked_at != null);
}

/** A `service_state` frame's `as_of` as a comparable number at MICROSECOND
 *  precision (astra round 2). `Date.parse` keeps milliseconds only, so two
 *  announces read inside one millisecond — the sweep's lock, then the
 *  purchase that was waiting on its row lock — compared equal and the older
 *  could overwrite the newer. The fraction is trimmed to three digits before
 *  the engine parses it (every engine takes three) and the digits past them
 *  are read back from the string. NaN for a missing or unparseable stamp. */
export function asOfKey(iso: string | null | undefined): number {
  if (!iso) return NaN;
  const ms = Date.parse(iso.replace(/(\.\d{3})\d+/, "$1"));
  if (!Number.isFinite(ms)) return NaN;
  const micros = /\.\d{3}(\d{1,3})/.exec(iso)?.[1];
  return ms + (micros ? Number(micros.padEnd(3, "0")) / 1000 : 0);
}

/** The grace banner shows ONLY while enforcement is in force for this
 *  account, the account is in grace, no alarm or No Data alarm is live (the
 *  cycle-464 rule: it renders nothing then and never covers an alarm
 *  control), and never on the kiosk (its card carries the date instead). */
export function graceBannerVisible(input: {
  sub: Pick<Subscription, "enforced" | "state" | "grace_ends_at"> | null | undefined;
  alarmLive: boolean;
  noDataAlarmActive: boolean;
  platform: BillingPlatform;
}): boolean {
  const { sub, alarmLive, noDataAlarmActive, platform } = input;
  if (!sub || !sub.enforced || sub.state !== "grace") return false;
  if (alarmLive || noDataAlarmActive) return false;
  if (platform === "kiosk") return false;
  return graceBannerLine(sub.grace_ends_at) != null;
}

/** Routes a LOCKED owner can still reach: Settings (Subscription, Restore,
 *  delete account), sign-in and recovery, pairing, the admin console. A
 *  locked person must never be trapped. Everything else shows the lockout. */
export const LOCKOUT_REACHABLE_PREFIXES = [
  "/settings",
  "/login",
  "/reset-password",
  "/forgot-password",
  "/pair",
  "/pair-approve",
  "/pair-device",
  "/admin",
] as const;

export function lockoutCoversPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return !LOCKOUT_REACHABLE_PREFIXES.some((pre) => p === pre || p.startsWith(`${pre}/`) || p.startsWith(`${pre}?`));
}

/** A member's lock from the two sources the client holds: the WS
 *  `service_state` map WINS whenever it has an entry (it is live — an
 *  unlock frame arrives seconds after the grant), the REST dashboard row
 *  is the fallback until the first frame (it is a seed that can be minutes
 *  old — safety review M2: a stale `service_locked: true` row must not
 *  keep a resumed member's number hidden). Absent on both = active. */
export function memberLockedFrom(
  wsValue: boolean | undefined,
  rowValue: boolean | null | undefined,
): boolean {
  if (wsValue != null) return wsValue === true;
  return rowValue === true;
}

/** The per-member lock map's answer for one member (absent = active). */
export function serviceLockedFor(
  byMember: Record<string, boolean> | null | undefined,
  memberId: string | null | undefined,
): boolean {
  if (!byMember || !memberId) return false;
  return byMember[memberId] === true;
}
